// pages/superadmin/DealValues.jsx
//
// Phase 5, Deal Values admin screen. Each product (wizzyl, axo_signal,
// 6ela, telco) has an AXO-wide £ value used by the daily briefing to
// estimate pipeline, plus optional per-partner overrides. Until a
// product's global value is set here, the briefing falls back to the
// flagged £400 placeholder (dealValueResolver.UNCONFIGURED_DEFAULT_DEAL_VALUE_GBP)
//, this screen is what closes that gap: it's the "I need the real deal
// values from you" ask from the Phase 5 governance/commercial-numbers work.
//
// Styling matches AiGovernanceSettings.jsx / AiPerformanceDashboard.jsx
// exactly (same CSS custom properties, same header/card/table patterns).
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import apiService from "../../../services/api";
import NotificationBell from "../../../components/NotificationBell";
import { useAuth } from "../../../contexts/AuthContext";
import { Select } from "../../../arc/components/select/select";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import { MoneyInput } from "../../../arc/components/money-input/money-input";
import InfoTooltip from "../../../components/InfoTooltip";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; }
  html, body { overflow-x: hidden; }

  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1000px; margin: 0 auto; padding: 0 28px; min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .dash-brand { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); }
  .btn-back:hover { background: rgba(255,255,255,0.15); border-color: rgba(255,255,255,0.22); color: #fff; }
  .dash-brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--radius-sm); background: var(--accent); box-shadow: 0 0 0 1px rgba(255,255,255,0.14) inset, 0 4px 10px rgba(3,12,28,0.35); color: #fff; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(255,255,255,0.42); }
  .dash-header-actions { display: flex; align-items: center; gap: 8px; padding: 12px 0; }
  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.8); cursor: pointer; transition: all var(--t); white-space: nowrap; text-decoration: none; }
  .btn-header:hover { background: rgba(255,255,255,0.15); border-color: rgba(255,255,255,0.24); color: #fff; }
  .btn-header:focus-visible { outline: 2px solid #6ea1f7; outline-offset: 2px; }
  .btn-header.is-refreshing svg { animation: refresh-spin 0.6s linear; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }

  .dash-main { max-width: 1000px; margin: 0 auto; padding: 32px 28px 48px; display: flex; flex-direction: column; gap: 28px; min-width: 0; }

  .section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
  .section-title { display: flex; align-items: center; gap: 9px; font-family: var(--mono); font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); font-weight: 500; }
  .section-title > .axo-info-tip { margin-inline-start: -5px; }
  .section-title::before { content: ""; width: 3px; height: 13px; border-radius: 4px; background: var(--accent-mid); flex-shrink: 0; }
  .dash-root.dark .section-title::before { background: #6ea1f7; }
  .section-caption { font-size: 11.5px; color: var(--text-faint); }

  .gov-table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .gov-row { display: flex; align-items: center; gap: 14px; padding: 16px 20px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
  .gov-row:last-child { border-bottom: none; }
  .gov-row-main { flex: 1 1 200px; min-width: 0; }
  .gov-task-name { font-size: 13.5px; font-weight: 600; color: var(--text); }
  .gov-task-note { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); margin-top: 3px; }

  .gov-field { display: flex; flex-direction: column; gap: 4px; }
  .gov-money { --money-input-height: 40px; --text-3xl: 16px; --text-2xl: 15px; --radius-panel: var(--radius); width: 150px; }
  .gov-field label { font-family: var(--mono); font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-faint); }
  .gov-select, .gov-input { font-family: var(--sans); font-size: 12.5px; padding: 6px 8px; border: 1px solid var(--border-strong); border-radius: var(--radius-xs); background: var(--surface); color: var(--text); min-width: 0; }
  .gov-select { min-width: 150px; }
  .gov-input.value { width: 110px; font-family: var(--mono); }
  .gov-select:focus, .gov-input:focus { outline: 2px solid var(--accent-mid); outline-offset: 1px; }

  .gov-status { font-family: var(--mono); font-size: 10px; padding: 3px 8px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
  .gov-status.saved { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
  .gov-status.saving { background: var(--accent-dim); color: var(--accent-mid); border: 1px solid var(--border-strong); }
  .gov-status.error { background: var(--red-dim); color: var(--red); border: 1px solid var(--red-border); }
  .dash-root.dark .gov-status.saved { color: #34d399; }

  .source-badge { font-family: var(--mono); font-size: 10px; padding: 3px 8px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
  .source-badge.global { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
  .source-badge.unconfigured_default { background: var(--amber-dim); color: var(--amber); border: 1px solid var(--amber-border); }
  .source-badge.partner_override { background: var(--accent-dim); color: var(--accent-mid); border: 1px solid var(--border-strong); }
  .dash-root.dark .source-badge.partner_override { color: #8fb6f8; }

  .btn-icon { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; flex-shrink: 0; background: transparent; border: 1px solid var(--border-strong); border-radius: var(--radius-xs); color: var(--text-faint); cursor: pointer; transition: all var(--t); }
  .btn-icon:hover { color: var(--red); border-color: var(--red-border); background: var(--red-dim); }
  .btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }

  .override-partner { flex: 1 1 160px; min-width: 0; }
  .override-partner-name { font-size: 13px; font-weight: 500; color: var(--text); overflow-wrap: anywhere; }
  .override-partner-id { font-family: var(--mono); font-size: 10px; color: var(--text-faint); margin-top: 2px; }

  .add-override-form { display: flex; align-items: flex-end; gap: 14px; padding: 16px 20px; flex-wrap: wrap; background: var(--surface-sub); border-top: 1px solid var(--border); }
  .btn-primary { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; background: var(--accent-mid); border: 1px solid var(--accent-mid); border-radius: 8px; color: #fff; cursor: pointer; transition: background var(--t), color var(--t), border-color var(--t), opacity var(--t); white-space: nowrap; }
  .btn-primary:focus-visible { outline: 2px solid var(--accent-mid); outline-offset: 2px; }
  .btn-primary:hover { background: var(--accent); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .form-error { font-size: 11.5px; color: var(--red); }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 20px; gap: 10px; text-align: center; }
  .empty-state-label { font-family: var(--mono); font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-faint); }
  .empty-state-sub { font-size: 12px; color: var(--text-faint); max-width: 360px; }
  .spinner { width: 26px; height: 26px; border: 2px solid var(--accent-dim); border-top-color: var(--accent-mid); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .btn-header, .btn-back { transform: translateY(0); }
  .btn-header:active, .btn-back:active { transform: translateY(1px) scale(0.97); }

  @media (max-width: 640px) {
    .dash-header-inner { padding: 12px 16px; }
    .dash-brand, .dash-header-actions { padding: 0; }
    .dash-main { padding: 24px 16px 40px; gap: 22px; }
    .gov-row { padding: 14px 16px; }
    .add-override-form { padding: 14px 16px; }
    .btn-header span.btn-label { display: none; }
  }
`;

const PRODUCT_LABELS = {
  wizzyl: "Wizzyl",
  axo_signal: "AXO Signal",
  "6ela": "6ELA",
  telco: "Telco",
};

// Arc MoneyInput speaks minor units (pence); the API stores pounds.
const toMinorGbp = (v) =>
  v === "" || v == null ? null : Math.round(Number(v) * 100);

function formatDelay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function DealValues({ backTo = "/super-admin/ai-governance" }) {
  const { logout } = useAuth();
  const [dealValues, setDealValues] = useState([]);
  const [products, setProducts] = useState([]);
  const [unconfiguredDefaultGbp, setUnconfiguredDefaultGbp] = useState(400);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // draft edits for the global-value inputs, keyed by product
  const [globalDrafts, setGlobalDrafts] = useState({});
  // rowStatus[key] = 'saving' | 'saved' | 'error', keyed by product for
  // global rows and by row id for overrides
  const [rowStatus, setRowStatus] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const [newOverride, setNewOverride] = useState({
    product: "",
    customerId: "",
    valueGbp: "",
  });
  const [addingOverride, setAddingOverride] = useState(false);
  const [addError, setAddError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [dealValuesRes, customersRes] = await Promise.all([
        apiService.getDealValues(),
        apiService.getCustomers().catch(() => ({ data: [] })),
      ]);
      setDealValues(dealValuesRes.dealValues || []);
      setProducts(dealValuesRes.products || []);
      setUnconfiguredDefaultGbp(dealValuesRes.unconfiguredDefaultGbp ?? 400);
      setCustomers(customersRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load deal values");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
  };

  const customerName = useCallback(
    (id) => customers.find((c) => c.id === id)?.name || id,
    [customers],
  );

  // One row per product: the configured global row if it exists, else a
  // virtual "unconfigured" placeholder using the flagged default.
  const globalRows = useMemo(() => {
    return products.map((product) => {
      const configured = dealValues.find(
        (dv) => dv.product === product && !dv.customerId,
      );
      if (configured) {
        return {
          product,
          id: configured.id,
          valueGbp: Number(configured.valueGbp),
          source: "global",
        };
      }
      return {
        product,
        id: null,
        valueGbp: unconfiguredDefaultGbp,
        source: "unconfigured_default",
      };
    });
  }, [products, dealValues, unconfiguredDefaultGbp]);

  const overrideRows = useMemo(
    () => dealValues.filter((dv) => dv.customerId),
    [dealValues],
  );

  const draftValue = (product, fallback) =>
    globalDrafts[product] !== undefined ? globalDrafts[product] : fallback;

  const handleGlobalDraftChange = (product, value) => {
    setGlobalDrafts((prev) => ({ ...prev, [product]: value }));
  };

  const handleGlobalBlur = async (product, value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setGlobalDrafts((prev) => {
        const next = { ...prev };
        delete next[product];
        return next;
      });
      return;
    }
    setRowStatus((prev) => ({ ...prev, [product]: "saving" }));
    try {
      const res = await apiService.upsertDealValue({
        product,
        customerId: null,
        valueGbp: parsed,
      });
      setDealValues((prev) => {
        const withoutGlobal = prev.filter(
          (dv) => !(dv.product === product && !dv.customerId),
        );
        return [...withoutGlobal, res.dealValue];
      });
      setGlobalDrafts((prev) => {
        const next = { ...prev };
        delete next[product];
        return next;
      });
      setRowStatus((prev) => ({ ...prev, [product]: "saved" }));
      await formatDelay(1400);
      setRowStatus((prev) => ({ ...prev, [product]: undefined }));
    } catch (err) {
      setRowStatus((prev) => ({ ...prev, [product]: "error" }));
    }
  };

  const handleDelete = async (row) => {
    if (!row.id) return; // nothing configured yet, already at default
    const statusKey = row.customerId ? row.id : row.product;
    setDeletingId(row.id);
    setRowStatus((prev) => ({ ...prev, [statusKey]: "saving" }));
    try {
      await apiService.deleteDealValue(row.id);
      setDealValues((prev) => prev.filter((dv) => dv.id !== row.id));
      setRowStatus((prev) => ({ ...prev, [statusKey]: undefined }));
    } catch (err) {
      setRowStatus((prev) => ({ ...prev, [statusKey]: "error" }));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddOverride = async (e) => {
    e.preventDefault();
    setAddError(null);
    const value = Number(newOverride.valueGbp);
    if (!newOverride.product) {
      setAddError("Choose a product");
      return;
    }
    if (!newOverride.customerId) {
      setAddError("Choose a partner");
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      setAddError("Value must be a positive number");
      return;
    }
    setAddingOverride(true);
    try {
      const res = await apiService.upsertDealValue({
        product: newOverride.product,
        customerId: newOverride.customerId,
        valueGbp: value,
      });
      setDealValues((prev) => {
        const withoutSame = prev.filter(
          (dv) =>
            !(
              dv.product === newOverride.product &&
              dv.customerId === newOverride.customerId
            ),
        );
        return [...withoutSame, res.dealValue];
      });
      setNewOverride({ product: "", customerId: "", valueGbp: "" });
    } catch (err) {
      setAddError(
        err.response?.data?.message || "Failed to save partner override",
      );
    } finally {
      setAddingOverride(false);
    }
  };

  return (
    <div className="dash-root">
      <style>{styles}</style>

      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link
              to={backTo}
              className="btn-back"
              title="Back to AI Governance"
              aria-label="Back to AI Governance"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <div className="dash-brand-mark">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.5v13M4 4.5h5.5a2 2 0 010 4H6.5a2 2 0 000 4H12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="dash-brand-text">
              <div className="dash-brand-name">Deal Values</div>
              <div className="dash-brand-sub">Pipeline Estimate Inputs</div>
            </div>
          </div>
          <div className="dash-header-actions">
            <NotificationBell />
            <button
              type="button"
              className={`btn-header${refreshing ? " is-refreshing" : ""}`}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 2.5V6h-3.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="btn-label">Refresh</span>
            </button>
            <button type="button" className="btn-header" onClick={logout}>
              <span className="btn-label">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <section>
          <div className="section-head">
            <div className="section-title">
              AXO-wide Values
              <InfoTooltip
                label="What are AXO-wide values?"
                content="The default deal value used for each product when AXO estimates pipeline in the daily briefing. A partner override can replace any of these for one partner."
              />
            </div>
            <div className="section-caption">
              Used by the daily briefing to estimate pipeline. Products without
              a value here fall back to the flagged £{unconfiguredDefaultGbp}{" "}
              placeholder.
            </div>
          </div>

          {loading ? (
            <div className="empty-state" style={{ width: "100%" }}>
              <Skeleton lines={5} label="Loading deal values" />
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-state-label">{error}</div>
            </div>
          ) : (
            <div className="gov-table-wrap">
              {globalRows.map((row) => (
                <div className="gov-row" key={row.product}>
                  <div className="gov-row-main">
                    <div className="gov-task-name">
                      {PRODUCT_LABELS[row.product] || row.product}
                    </div>
                    <div className="gov-task-note">{row.product}</div>
                  </div>

                  <span className={`source-badge ${row.source}`}>
                    {row.source === "global"
                      ? "Configured"
                      : `Unconfigured, using £${Number(
                          unconfiguredDefaultGbp || 0,
                        ).toLocaleString()}`}
                  </span>

                  <div className="gov-field">
                    <label>Value (£)</label>
                    <MoneyInput
                      id={`value-${row.product}`}
                      label="Value (£)"
                      hideLabel
                      className="gov-money"
                      currency="GBP"
                      currencies={["GBP"]}
                      locale="en-GB"
                      quickAdd={[]}
                      value={toMinorGbp(draftValue(row.product, row.valueGbp))}
                      onValueChange={(minor, details) =>
                        handleGlobalDraftChange(row.product, details.major)
                      }
                      onBlur={() =>
                        handleGlobalBlur(
                          row.product,
                          draftValue(row.product, row.valueGbp),
                        )
                      }
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-icon"
                    title={
                      row.id
                        ? "Remove configured value (reverts to default)"
                        : "Already using the default"
                    }
                    aria-label="Remove configured value"
                    disabled={!row.id || deletingId === row.id}
                    onClick={() => handleDelete(row)}
                  >
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 4h10M6.5 4V2.5h3V4M4.5 4l.6 9a1 1 0 001 .9h3.8a1 1 0 001-.9l.6-9"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {rowStatus[row.product] && (
                    <span className={`gov-status ${rowStatus[row.product]}`}>
                      {rowStatus[row.product] === "saving"
                        ? "Saving…"
                        : rowStatus[row.product] === "saved"
                          ? "Saved"
                          : "Failed"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {!loading && !error && (
          <section>
            <div className="section-head">
              <div className="section-title">
                Partner Overrides
                <InfoTooltip
                  label="What are partner overrides?"
                  content="Optional per-partner deal values. When set, an override is used instead of the AXO-wide value for that partner's pipeline estimates."
                />
              </div>
              <div className="section-caption">
                Optional, takes priority over the AXO-wide value for that
                partner only.
              </div>
            </div>

            <div className="gov-table-wrap">
              {overrideRows.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-label">No overrides set</div>
                  <div className="empty-state-sub">
                    Every partner uses the AXO-wide value above until one is
                    added here.
                  </div>
                </div>
              ) : (
                overrideRows.map((row) => (
                  <div className="gov-row" key={row.id}>
                    <div className="gov-row-main">
                      <div className="gov-task-name">
                        {PRODUCT_LABELS[row.product] || row.product}
                      </div>
                      <div className="gov-task-note">{row.product}</div>
                    </div>

                    <div className="override-partner">
                      <div className="override-partner-name">
                        {customerName(row.customerId)}
                      </div>
                      <div className="override-partner-id">
                        {row.customerId}
                      </div>
                    </div>

                    <span className="source-badge partner_override">
                      £{Number(row.valueGbp).toLocaleString()}
                    </span>

                    <button
                      type="button"
                      className="btn-icon"
                      title="Remove override"
                      aria-label="Remove override"
                      disabled={deletingId === row.id}
                      onClick={() => handleDelete(row)}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M3 4h10M6.5 4V2.5h3V4M4.5 4l.6 9a1 1 0 001 .9h3.8a1 1 0 001-.9l.6-9"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {rowStatus[row.id] && (
                      <span className={`gov-status ${rowStatus[row.id]}`}>
                        {rowStatus[row.id] === "saving"
                          ? "Saving…"
                          : rowStatus[row.id] === "saved"
                            ? "Saved"
                            : "Failed"}
                      </span>
                    )}
                  </div>
                ))
              )}

              <form className="add-override-form" onSubmit={handleAddOverride}>
                <div className="gov-field">
                  <Select
                    label="Product"
                    className="gov-select"
                    placeholder="Select product…"
                    value={newOverride.product}
                    onValueChange={(value) =>
                      setNewOverride((prev) => ({ ...prev, product: value }))
                    }
                    options={products.map((p) => ({
                      value: p,
                      label: PRODUCT_LABELS[p] || p,
                    }))}
                  />
                </div>

                <div className="gov-field">
                  <Select
                    label="Partner"
                    className="gov-select"
                    placeholder="Select partner…"
                    value={newOverride.customerId}
                    onValueChange={(value) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        customerId: value,
                      }))
                    }
                    options={customers.map((c) => ({
                      value: String(c.id),
                      label: c.name,
                    }))}
                  />
                </div>

                <div className="gov-field">
                  <label>Value (£)</label>
                  <MoneyInput
                    id="override-value"
                    label="Value (£)"
                    hideLabel
                    className="gov-money"
                    currency="GBP"
                    currencies={["GBP"]}
                    locale="en-GB"
                    quickAdd={[]}
                    value={toMinorGbp(newOverride.valueGbp)}
                    onValueChange={(minor, details) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        valueGbp: details.major ?? "",
                      }))
                    }
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={addingOverride}
                >
                  {addingOverride ? "Adding…" : "Add Override"}
                </button>

                {addError && <div className="form-error">{addError}</div>}
              </form>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
